/**
 * Small localStorage-backed store for the Cognito tokens returned by the
 * backend-proxy auth endpoints. Kept framework-free so the api request helpers
 * can read the access token without importing React.
 */

const ACCESS_KEY = 'cgt.auth.accessToken'
const REFRESH_KEY = 'cgt.auth.refreshToken'
const EMAIL_KEY = 'cgt.auth.email'
const SESSION_KEY = 'cgt.auth.session'

export interface StoredAuth {
  accessToken: string
  refreshToken?: string
  email?: string
}

/**
 * The authenticated principal, as returned by `GET /auth/me` — roles and
 * effective permissions resolved server-side. Cached so the UI can gate
 * routes/actions without a round-trip on every render.
 */
export interface AuthSession {
  userId: string | null
  sub: string
  username?: string
  email?: string
  // Name parts plus the display name derived from them, so the profile form can
  // bind directly instead of splitting `fullName` on a space and guessing where
  // a two-word surname belongs. Null for a principal with no DB row yet.
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  roles: string[]
  /**
   * Permissions OUTSIDE any tenant: the shell baseline only. Real authorization
   * lives in tenantPermissions, keyed by tenant id.
   */
  permissions: string[]
  tenantPermissions: Record<string, string[]>
  /** Superadmin: platform-wide access, bypasses every check. */
  fullAccess: boolean
  enabled: boolean
  /** Password login is off; the Cognito password is only a signing password. Absent in older cached sessions. */
  ssoOnly?: boolean
  /** This session came from an SSO sign-in. */
  sso?: boolean
  // Tenant membership ids (empty for full-access principals resolved purely
  // from token claims). Mirrors the backend RequestUser.
  tenantIds: string[]
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function getEmail(): string | null {
  return localStorage.getItem(EMAIL_KEY)
}

export function setAuth({ accessToken, refreshToken, email }: StoredAuth): void {
  localStorage.setItem(ACCESS_KEY, accessToken)
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken)
  if (email) localStorage.setItem(EMAIL_KEY, email)
}

export function getSession(): AuthSession | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as AuthSession
    // Sessions cached before memberships existed lack the array — normalize so
    // consumers never see undefined. Refreshed on the next /auth/me fetch.
    parsed.tenantIds ??= []
    parsed.tenantPermissions ??= {}
    return parsed
  } catch {
    return null
  }
}

export function setSession(session: AuthSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearAuth(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(EMAIL_KEY)
  localStorage.removeItem(SESSION_KEY)
  localStorage.removeItem('cgt_selected_tenant')
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken())
}

// Expiry (epoch ms) of the stored Cognito access token, decoded from its `exp`
// claim. Returns null when there's no token or it can't be parsed.
export function getAccessTokenExpiryMs(): number | null {
  const token = getAccessToken()
  if (!token) return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(b64)) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

// True when there's a token and its expiry is in the past.
export function isSessionExpired(): boolean {
  const exp = getAccessTokenExpiryMs()
  return exp !== null && exp <= Date.now()
}
